type MatchedDiv = {
  divIndex: number;
  offset: number;
};

type MatchedPhraseToHighlight = {
  begin: MatchedDiv;
  end: MatchedDiv;
};

/**
 * TextHighlighter handles highlighting matches from the FindController in
 * either the text layer or XFA layer depending on the type of document.
 * It was inspired by pdf.js/web/text_highlighter.js
 */
export class PDFTextHighlighter {
  private parentDiv: HTMLElement | null;
  private textDivs: HTMLElement[] = [];
  private textContentItemsStr: string[] = [];

  private matches: MatchedPhraseToHighlight[] = [];
  private selectedMatchElements: NodeListOf<Element> | undefined = undefined;

  constructor(parentDiv: HTMLElement | null) {
    this.parentDiv = parentDiv;
  }

  private clearAllHighlights = () => {
    let clearedUntilDivIndex = -1;

    // Clear all current matches.
    for (const match of this.matches) {
      const begin = Math.max(clearedUntilDivIndex, match.begin.divIndex);
      for (let n = begin, end = match.end.divIndex; n <= end; n++) {
        const div = this.textDivs[n];
        div.textContent = this.textContentItemsStr[n];
        div.className = '';
      }
      clearedUntilDivIndex = match.end.divIndex + 1;
    }
  };

  private convertMatches = (matches: number[], matchesLength: number[]) => {
    // Early exit if there is nothing to convert.
    if (!matches) {
      return [];
    }
    const { textContentItemsStr } = this;

    let i = 0;
    let iIndex = 0;
    const end = textContentItemsStr.length - 1;
    const result = [];

    for (let m = 0, mm = matches.length; m < mm; m++) {
      // Calculate the start position.
      let matchIndex = matches[m];

      // Loop over the divIndexs.
      while (i !== end && matchIndex >= iIndex + textContentItemsStr[i].length) {
        iIndex += textContentItemsStr[i].length;
        i++;
      }

      if (i === textContentItemsStr.length) {
        console.error('Could not find a matching mapping');
      }

      const beginMatch = {
        divIndex: i,
        offset: matchIndex - iIndex,
      };

      // Calculate the end position.
      matchIndex += matchesLength[m];

      // Somewhat the same array as above, but use > instead of >= to get
      // the end position right.
      while (i !== end && matchIndex > iIndex + textContentItemsStr[i].length) {
        iIndex += textContentItemsStr[i].length;
        i++;
      }

      const endMatch = {
        divIndex: i,
        offset: matchIndex - iIndex,
      };
      result.push({
        begin: beginMatch,
        end: endMatch,
      });
    }
    return result;
  };

  private beginText = ({
    begin,
    matchIndex,
    className = '',
  }: {
    begin: MatchedDiv;
    className?: string;
    matchIndex: number;
  }) => {
    const divIndex = begin.divIndex;
    this.textDivs[divIndex].textContent = '';
    return this.appendTextToDiv({ className, divIndex, fromOffset: 0, matchIndex, toOffset: begin.offset });
  };

  private appendTextToDiv = ({
    divIndex,
    fromOffset,
    toOffset,
    className = '',
    matchIndex,
  }: {
    divIndex: number;
    fromOffset: number;
    toOffset: number | undefined;
    className?: string;
    matchIndex: number;
  }) => {
    let div = this.textDivs[divIndex];
    if (div.nodeType === Node.TEXT_NODE) {
      const span = document.createElement('span');
      div.before(span);
      span.append(div);
      this.textDivs[divIndex] = span;
      div = span;
    }
    const content = this.textContentItemsStr[divIndex].substring(fromOffset, toOffset);
    const node = document.createTextNode(content);
    if (className) {
      const span = document.createElement('span');
      span.className = `${className} appended`;
      span.dataset.matchedIndex = matchIndex.toString();
      span.dataset.testid = `PDF_SEARCH_RESULT`;
      span.append(node);
      div.append(span);
    } else {
      div.append(node);
    }
  };

  private renderMatches = () => {
    // Early exit if there is nothing to render.
    if (this.matches.length === 0) {
      return;
    }

    let prevEnd = null;
    const infinity = {
      divIndex: -1,
      offset: undefined,
    };

    let lastDivIndex = -1;
    let lastOffset = -1;
    for (let matchIndex = 0; matchIndex < this.matches.length; matchIndex++) {
      const match = this.matches[matchIndex];
      const begin = match.begin;
      if (begin.divIndex === lastDivIndex && begin.offset === lastOffset) {
        // It's possible to be in this situation if we searched for a 'f' and we
        // have a ligature 'ff' in the text. The 'ff' has to be highlighted two
        // times.
        continue;
      }
      lastDivIndex = begin.divIndex;
      lastOffset = begin.offset;

      const end = match.end;

      // Match inside new div.
      if (!prevEnd || begin.divIndex !== prevEnd.divIndex) {
        // If there was a previous div, then add the text at the end.
        if (prevEnd !== null) {
          this.appendTextToDiv({
            divIndex: prevEnd.divIndex,
            fromOffset: prevEnd.offset,
            matchIndex,
            toOffset: infinity.offset,
          });
        }
        // Clear the divs and set the content until the starting point.
        this.beginText({ begin, matchIndex });
      } else {
        this.appendTextToDiv({
          divIndex: prevEnd.divIndex,
          fromOffset: prevEnd.offset,
          matchIndex,
          toOffset: begin.offset,
        });
      }

      if (begin.divIndex === end.divIndex) {
        this.appendTextToDiv({
          className: 'highlight',
          divIndex: begin.divIndex,
          fromOffset: begin.offset,
          matchIndex,
          toOffset: end.offset,
        });
      } else {
        this.appendTextToDiv({
          className: 'highlight begin',
          divIndex: begin.divIndex,
          fromOffset: begin.offset,
          matchIndex,
          toOffset: infinity.offset,
        });
        for (let n0 = begin.divIndex + 1, n1 = end.divIndex; n0 < n1; n0++) {
          this.textDivs[n0].className = 'highlight middle';
        }
        this.beginText({ begin: end, className: 'highlight end', matchIndex });
      }
      prevEnd = end;
    }

    if (prevEnd) {
      this.appendTextToDiv({
        divIndex: prevEnd.divIndex,
        fromOffset: prevEnd.offset,
        matchIndex: this.matches.length - 1,
        toOffset: infinity.offset,
      });
    }
  };

  /**
   * Store two arrays that will map DOM nodes to text they should contain.
   * The arrays should be of equal length and the array element at each index
   * should correspond to the other. e.g.
   * `items[0] = "<span>Item 0</span>" and texts[0] = "Item 0";
   *
   * @param {Array<Node>} divs
   * @param {Array<string>} texts
   */
  setTextMapping = (divs: HTMLElement[], texts: string[]) => {
    this.textDivs = divs;
    this.textContentItemsStr = texts;
  };

  /**
   * Highlight matched phrases
   */
  highlightMatchedPhrases = ({
    pageMatches,
    pageMatchesLength,
  }: {
    pageMatches: number[];
    pageMatchesLength: number[];
  }) => {
    this.clearAllHighlights();
    if (!this.textDivs.length || !this.textContentItemsStr.length) return;
    if (!pageMatches?.length || !pageMatchesLength?.length) return;
    this.matches = this.convertMatches(pageMatches, pageMatchesLength);
    this.renderMatches();
  };

  clearSelectedMatch = () => {
    this.selectedMatchElements?.forEach((element) => {
      element.classList.remove('selected');
    });
    this.selectedMatchElements = undefined;
  };

  changeSelectedMatch = (selectedMatchIndex: number) => {
    this.clearSelectedMatch();

    // matched phrases may be spread across multiple elements e.g. when they are hyphenated
    this.selectedMatchElements = this.parentDiv?.querySelectorAll(`[data-matched-index="${selectedMatchIndex}"]`);
    this.selectedMatchElements?.forEach((element) => {
      element.classList.add('selected');
    });
    return this.selectedMatchElements;
  };

  cleanup = () => {
    this.clearAllHighlights();
    this.clearSelectedMatch();
  };
}
